import { createFileRoute, useParams, useRouter } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
    userSummaryQueryOptions,
    userFullProfileQueryOptions,
} from '@/hooks/queries/optimizedUserProfileQueryOptions'
import { isApiError } from '@wtf/shared'
import { ProfileHeader } from '@/components/profile/ProfileHeader'
import { ProfileInfo } from '@/components/profile/ProfileInfo'
import { PrivateProfileView } from '@/components/profile/PrivateProfileView'
import { TabNavigation } from '@/components/shared/molecules/TabNavigation'
import { CollectionGrid } from '@/components/collections/CollectionGrid'
// import { CollectionPrivateGrid } from '@/components/collections/CollectionPrivateGrid'
import { ProfileActivityTab } from '@/components/profile/ProfileActivityTab'
import { ProfileOptionsBottomSheet } from '@/components/profile/bottom-sheets/ProfileOptionsBottomSheet'
import { ReportProfileBottomSheet } from '@/components/profile/bottom-sheets/ReportProfileBottomSheet'
import { BlockUserBottomSheet } from '@/components/profile/bottom-sheets/BlockUserBottomSheet'
import { ViewFollowersBottomSheet } from '@/components/profile/bottom-sheets/ViewFollowersBottomSheet'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import ProfileBanner from '@/components/profile/desktop/ProfileBanner'
import { STANDARD_PRODUCT_GRID_SPACING } from '@/constants/gridBreakpoints'
import { TryOnGrid } from '@/components/try-on/TryOnGrid'
import { EyeOff } from 'lucide-react'
import { AnalyticsContext } from '@/utils/analytics'

const baseTabs = [
    { value: 'collections', label: 'Collections' },
    { value: 'activity', label: 'Activity' },
]

const tryOnsTab = { value: 'try-ons', label: 'Try-Ons', icon: EyeOff }

// Skeleton components for loading states - improved to match activity skeleton style
const ProfileSkeleton = () => (
    <div className="space-y-6 px-2 py-6 md:px-6">
        {/* Avatar and bio skeleton - enhanced card style */}
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-center space-y-4">
                <div className="h-24 w-24 animate-pulse rounded-full bg-gray-200" />
                <div className="space-y-2 text-center">
                    <div className="mx-auto h-5 w-36 animate-pulse rounded bg-gray-200" />
                    <div className="mx-auto h-4 w-48 animate-pulse rounded bg-gray-100" />
                </div>
            </div>

            {/* Stats skeleton with better spacing */}
            <div className="mt-6 flex justify-center space-x-12 border-t border-gray-50 pt-6">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2 text-center">
                        <div className="mx-auto h-6 w-10 animate-pulse rounded bg-gray-200" />
                        <div className="mx-auto h-3 w-16 animate-pulse rounded bg-gray-100" />
                    </div>
                ))}
            </div>
        </div>
    </div>
)

const CollectionsSkeleton = () => (
    <div className="px-2 md:px-6 md:py-4">
        <div
            className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 ${STANDARD_PRODUCT_GRID_SPACING.container}`}
        >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div
                    key={i}
                    className={`animate-pulse rounded-2xl border border-gray-100 bg-white p-3 shadow-sm ${STANDARD_PRODUCT_GRID_SPACING.items}`}
                >
                    <div className="mb-3 aspect-square rounded-xl bg-gray-200" />
                    <div className="space-y-2">
                        <div className="h-4 w-3/4 rounded bg-gray-200" />
                        <div className="h-3 w-1/2 rounded bg-gray-100" />
                    </div>
                </div>
            ))}
        </div>
    </div>
)

const ActivitySkeleton = () => (
    <div className="flex min-h-[360px] flex-col gap-2 rounded-xl bg-white p-2">
        {[1, 2, 3].map((i) => (
            <div
                key={i}
                className="flex animate-pulse items-center rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
            >
                <div className="min-w-0 flex-1">
                    <div className="mb-2 h-4 w-3/4 rounded bg-gray-200" />
                    <div className="h-3 w-1/4 rounded bg-gray-100" />
                </div>
                <div className="ml-3 shrink-0">
                    <div className="h-14 w-14 rounded-xl bg-gray-200" />
                </div>
            </div>
        ))}
    </div>
)

export const Route = createFileRoute('/profiles/$id')({
    component: ProfileRoute,
    // Remove blocking loader - allow instant navigation
})

function ProfileRoute() {
    const router = useRouter()
    const { id } = useParams({ from: '/profiles/$id' })
    const { withAuth } = useProtectedAction()
    const { user: currentUser } = useCurrentUser()

    // UI state
    const [activeTab, setActiveTab] = useState<string>(baseTabs[0].value)
    const [openOptions, setOpenOptions] = useState(false)
    const [openReport, setOpenReport] = useState(false)
    const [openBlock, setOpenBlock] = useState(false)
    const [openViewFollowers, setOpenViewFollowers] = useState(false)

    // Track 403 error state for the duration of this component instance
    // Note: This resets on page refresh/remount - will re-check on next visit
    const [isPrivateProfileDetected, setIsPrivateProfileDetected] =
        useState(false)
    const hasDetectedPrivate = useRef(false)

    const queryClient = useQueryClient()

    // Stage 1: Load summary immediately for instant navigation
    const {
        data: summary,
        isLoading: summaryLoading,
        isError: summaryError,
        error: summaryErrorObj,
    } = useQuery(userSummaryQueryOptions(id))

    // Stage 2: Load full profile in background - completely disabled if private detected
    const {
        data: fullProfile,
        isError: fullProfileError,
        error: fullProfileErrorObj,
        isFetching: fullProfileFetching,
        isPlaceholderData: isFullProfilePlaceholder,
    } = useQuery({
        ...userFullProfileQueryOptions(queryClient, id, false),
        enabled: !isPrivateProfileDetected && !!id,
        retry: false, // Disable retries completely
        refetchOnWindowFocus: false, // Disable window focus refetch
        refetchOnReconnect: false, // Disable reconnect refetch
        refetchInterval: false, // Disable interval refetch
        refetchIntervalInBackground: false, // Disable background refetch
    })

    // Track 403 errors once detected and disable query for this component instance
    // Prevents repeated failed requests during the same session
    useEffect(() => {
        if (summary && fullProfileError && !hasDetectedPrivate.current) {
            const is403Error =
                (isApiError(fullProfileErrorObj) && fullProfileErrorObj.statusCode === 403) ||
                fullProfileErrorObj?.message?.includes('403') ||
                fullProfileErrorObj?.message?.includes('Forbidden')

            if (is403Error) {
                console.log(
                    '🔒 Private profile detected, disabling profile query for this session'
                )
                hasDetectedPrivate.current = true

                // Aggressively destroy all profile-related queries BEFORE setting state
                queryClient.cancelQueries({ queryKey: ['user', 'profile', id] })
                queryClient.removeQueries({ queryKey: ['user', 'profile', id] })
                queryClient.setQueryData(['user', 'profile', id], undefined)

                // Also try variations of the query key
                queryClient.cancelQueries({
                    predicate: (query) =>
                        query.queryKey.includes('profile') &&
                        query.queryKey.includes(id),
                })
                queryClient.removeQueries({
                    predicate: (query) =>
                        query.queryKey.includes('profile') &&
                        query.queryKey.includes(id),
                })

                // Set state last to trigger re-render with disabled query
                setIsPrivateProfileDetected(true)
            }
        }
    }, [summary, fullProfileError, fullProfileErrorObj, queryClient, id])

    // Determine distinct loading states
    const isLoadingSummary = summaryLoading
    const isLoadingFullProfile = !fullProfile || isFullProfilePlaceholder
    const hasRealProfileData = !!summary
    const hasRealCollectionsData = !!fullProfile && !isFullProfilePlaceholder

    // Use our permanent private profile detection state
    const isPrivateProfile = isPrivateProfileDetected

    // Use full profile if available, otherwise fall back to summary data
    // Memoize to prevent unnecessary re-renders of ProfileInfo
    const profile = useMemo(
        () =>
            fullProfile ||
            (summary
                ? {
                    ...summary,
                    subscribedToCount: 0,
                    subscriberCount: 0,
                    publicCollectionIds: [],
                    privateAndSavedCollectionIds: [],
                    createdAt: '',
                    updatedAt: '',
                }
                : null),
        [fullProfile, summary]
    )

    // Error handling: Only show error if summary failed (indicates user doesn't exist)
    const isError = summaryError
    const error = summaryErrorObj
    const isFetching = fullProfileFetching

    // Handlers
    const handleTabChange = (value: string) => setActiveTab(value)
    const handleOpenOptions = withAuth(() => setOpenOptions(true))
    const handleOpenReport = withAuth(() => {
        setOpenOptions(false)
        setOpenReport(true)
    })
    const handleOpenBlock = withAuth(() => {
        setOpenOptions(false)
        setOpenBlock(true)
    })

    // Collections data - only show real collections when we have actual data
    const publicCollectionIds = hasRealCollectionsData
        ? fullProfile!.publicCollectionIds
        : []
    const privateCollectionIds = hasRealCollectionsData
        ? fullProfile!.privateAndSavedCollectionIds
        : []
    const isMe = currentUser?.uid === id

    // Conditionally include Try-Ons tab for own profile
    const tabs = isMe ? [...baseTabs, tryOnsTab] : baseTabs

    // Prefetch full profile immediately after summary loads successfully
    // Skip prefetch if we've already detected this is a private profile
    useEffect(() => {
        if (summary && !fullProfile && !fullProfileFetching && !isPrivateProfileDetected) {
            queryClient.prefetchQuery(
                userFullProfileQueryOptions(queryClient, id, isPrivateProfileDetected)
            )
        }
    }, [summary, fullProfile, fullProfileFetching, isPrivateProfileDetected, queryClient, id])

    // Show error state (user doesn't exist)
    if (isError) {
        return (
            <div className="relative mx-auto flex min-h-dvh max-w-4xl flex-col items-center justify-center bg-white p-4 md:max-w-full">
                <div className="text-center">
                    <h2 className="mb-2 text-xl font-semibold">
                        Profile not found
                    </h2>
                    <p className="mb-4 text-gray-600">
                        {error?.message ||
                            "This profile doesn't exist or is unavailable."}
                    </p>
                    <button
                        onClick={() => window.history.back()}
                        className="rounded-lg bg-black px-4 py-2 text-white"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    // Show private profile view
    if (isPrivateProfile && summary) {
        return <PrivateProfileView userSummary={summary} isMe={isMe} />
    }

    return (
        <div className="relative mx-auto flex min-h-dvh max-w-4xl flex-col md:max-w-full">
            {/* Mobile version */}
            <div className="md:hidden">
                {/* Show skeleton header immediately, then real data when loaded */}
                {isLoadingSummary ? (
                    <div className="flex items-center justify-between border-b border-gray-100 p-4">
                        <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
                        <div className="h-6 w-24 animate-pulse rounded bg-gray-200" />
                        <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
                    </div>
                ) : (
                    <ProfileHeader
                        isMe={isMe}
                        hasBack={!isMe}
                        username={profile?.username ?? ''}
                        onMoreClick={handleOpenOptions}
                    />
                )}

                <div className="relative mb-24">
                    {/* Profile section: Show skeleton while summary loading, real profile after */}
                    {isLoadingSummary ? (
                        <ProfileSkeleton />
                    ) : (
                        <ProfileInfo profile={profile!} isMe={isMe} />
                    )}

                    {/* Subtle background loading indicator when upgrading from summary to full profile */}
                    {isFetching && hasRealProfileData && !isLoadingSummary && (
                        <div className="absolute top-2 right-2 h-2 w-2 animate-pulse rounded-full bg-gray-400 opacity-60" />
                    )}

                    {/* Tab navigation - show even while loading */}
                    <TabNavigation
                        tabs={tabs}
                        activeTab={activeTab}
                        setActiveTab={handleTabChange}
                        variant="profile"
                    />

                    {activeTab === 'collections' && (
                        <>
                            {isLoadingFullProfile ? (
                                <CollectionsSkeleton />
                            ) : (
                                <div
                                    className={`transition-opacity duration-300 ${isFetching && hasRealCollectionsData ? 'opacity-75' : 'opacity-100'}`}
                                >
                                    <CollectionGrid
                                        collectionIds={publicCollectionIds}
                                        context={(index) => AnalyticsContext.profilePage(id, 'profile_collections', undefined, index)}
                                    />
                                    {isMe && (
                                        <>
                                            <div className="my-3 px-2 md:my-6 md:px-6">
                                                <p className="text-base font-semibold text-black">
                                                    Private and saved
                                                    collections
                                                </p>
                                                <p className="text-13 text-[#858585]">
                                                    These do not appear on your
                                                    public profile.
                                                </p>
                                            </div>
                                            <CollectionGrid
                                                collectionIds={
                                                    privateCollectionIds
                                                }
                                                context={(index) => AnalyticsContext.profilePage(id, 'profile_collections', undefined, index)}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'activity' && (
                        <>
                            {isLoadingSummary ? (
                                <ActivitySkeleton />
                            ) : (
                                <ProfileActivityTab userId={id} isMe={isMe} />
                            )}
                        </>
                    )}

                    {activeTab === 'try-ons' && isMe && (
                        <div className="px-2 md:px-6">
                            <TryOnGrid archived={false} />
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop version */}
            <div className="mb-24 hidden md:block">
                {isLoadingSummary ? (
                    <div className="relative h-96 w-full animate-pulse overflow-hidden bg-gray-200">
                        <div className="absolute inset-0 bg-black/20" />
                        <div className="absolute top-0 right-0 left-0 z-20 grid grid-cols-12 justify-between p-6">
                            <div className="col-span-1">
                                <div className="h-5 w-5 animate-pulse rounded bg-white/30" />
                            </div>
                            <div className="col-span-9">
                                <div className="mb-2 h-6 w-32 animate-pulse rounded bg-white/30" />
                                <div className="h-4 w-48 animate-pulse rounded bg-white/20" />
                            </div>
                            <div className="col-span-2 flex justify-end space-x-4">
                                <div className="h-5 w-5 animate-pulse rounded bg-white/30" />
                                <div className="h-5 w-5 animate-pulse rounded bg-white/30" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <ProfileBanner
                        onArrowBack={() => router.history.back()}
                        onMore={handleOpenOptions}
                        profile={profile!}
                        isMe={isMe}
                    />
                )}

                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    setActiveTab={handleTabChange}
                    variant="profile"
                />

                {activeTab === 'collections' && (
                    <>
                        {isLoadingFullProfile ? (
                            <CollectionsSkeleton />
                        ) : (
                            <div
                                className={`px-6 transition-opacity duration-300 ${isFetching && hasRealCollectionsData ? 'opacity-75' : 'opacity-100'}`}
                            >
                                <CollectionGrid
                                    collectionIds={publicCollectionIds}
                                    context={(index) => AnalyticsContext.profilePage(id, 'profile_collections', undefined, index)}
                                />
                                {isMe && (
                                    <>
                                        <div className="mb my-3 px-2 md:my-6 md:px-6">
                                            <p className="text-base font-semibold text-black">
                                                Private and saved collections
                                            </p>
                                            <p className="text-13 text-[#858585]">
                                                These do not appear on your
                                                public profile.
                                            </p>
                                        </div>
                                        <CollectionGrid
                                            collectionIds={privateCollectionIds}
                                            context={(index) => AnalyticsContext.profilePage(id, 'profile_collections', undefined, index)}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'activity' && (
                    <>
                        {isLoadingSummary ? (
                            <ActivitySkeleton />
                        ) : (
                            <div className="container mx-auto max-w-1/2">
                                <ProfileActivityTab userId={id} isMe={isMe} />
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'try-ons' && isMe && (
                    <div className="px-6">
                        <TryOnGrid archived={false} />
                    </div>
                )}
            </div>

            {/* Bottom Sheets - only show when summary has loaded */}
            {!isLoadingSummary && (
                <div className="fixed">
                    <ProfileOptionsBottomSheet
                        open={openOptions}
                        onClose={() => setOpenOptions(false)}
                        onReportClick={handleOpenReport}
                        onBlockClick={handleOpenBlock}
                    />
                    <ReportProfileBottomSheet
                        open={openReport}
                        onClose={() => setOpenReport(false)}
                        userId={id}
                    />
                    <BlockUserBottomSheet
                        open={openBlock}
                        onClose={() => setOpenBlock(false)}
                        userId={id}
                        username={summary?.username}
                    />
                    <ViewFollowersBottomSheet
                        open={openViewFollowers}
                        setOpen={setOpenViewFollowers}
                    />
                </div>
            )}
        </div>
    )
}
